import { WEATHER_LAT, WEATHER_LON } from '../constants/weather';

export const TILE_SIZE_PX = 256;

/** Default radar view: ~this many miles from property to edge of map. */
export const DEFAULT_RADAR_RADIUS_MILES = 50;

/** Zoom-out steps (miles from center to edge). Default index = 50. */
export const RADAR_RADIUS_STEPS_MI: readonly number[] = [25, 50, 100, 200, 400, 650, 1000, 1600];

export const DEFAULT_RADAR_RADIUS_STEP_INDEX = RADAR_RADIUS_STEPS_MI.indexOf(
  DEFAULT_RADAR_RADIUS_MILES
);

const EARTH_CIRCUMFERENCE_MI = 24901;

export type MapTileCoord = {
  x: number;
  y: number;
  px: number;
  py: number;
};

export type RadarMapView = {
  zoom: number;
  radius: number;
  radiusMiles: number;
};

/** Approximate east-west width of one 256px tile at latitude (Web Mercator). */
export function tileWidthMilesLon(lat: number, zoom: number): number {
  const latRad = (lat * Math.PI) / 180;
  return (EARTH_CIRCUMFERENCE_MI * Math.cos(latRad)) / 2 ** zoom;
}

/** Tile grid radius (0 → 1 tile) so span ≈ 2×radiusMiles across. */
export function tileRadiusForMiles(lat: number, zoom: number, radiusMiles: number): number {
  const diameterMiles = radiusMiles * 2;
  const tileMi = tileWidthMilesLon(lat, zoom);
  const spanTiles = diameterMiles / tileMi;
  return Math.max(0, Math.ceil((spanTiles - 1) / 2));
}

const MAX_TILE_SPAN = 7;

/** Pick zoom + tile radius for a target radius in miles (caps grid at 7×7). */
export function radarMapViewForRadiusMiles(
  lat: number,
  lon: number,
  radiusMiles: number
): RadarMapView {
  let best: RadarMapView = { zoom: 7, radius: 1, radiusMiles };

  for (let zoom = 10; zoom >= 5; zoom--) {
    const radius = tileRadiusForMiles(lat, zoom, radiusMiles);
    const span = radius * 2 + 1;
    if (span > MAX_TILE_SPAN) continue;
    best = { zoom, radius, radiusMiles };
    break;
  }

  return best;
}

/** Latitude at the top edge of tile row y (Web Mercator). */
export function tileYToLat(y: number, zoom: number): number {
  const n = 2 ** zoom;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
  return (latRad * 180) / Math.PI;
}

export type TileGridBounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

/** Geographic bounds covered by a buildTileGrid() square. */
export function tileGridBounds(
  lat: number,
  lon: number,
  zoom: number,
  radius: number
): TileGridBounds {
  const { x: cx, y: cy } = latLonToTileXY(lat, lon, zoom);
  const n = 2 ** zoom;
  const minLon = ((cx - radius) / n) * 360 - 180;
  const maxLon = (((cx + radius + 1) / n) * 360) - 180;
  return {
    minLat: tileYToLat(cy + radius + 1, zoom),
    maxLat: tileYToLat(cy - radius, zoom),
    minLon,
    maxLon,
  };
}

/** Web Mercator tile X/Y for a lat/lon at zoom level z. */
export function latLonToTileXY(lat: number, lon: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom;
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return { x, y };
}

/** Pixel offset of lat/lon inside a buildTileGrid() square (top-left of grid = 0,0). */
export function latLonPixelInTileGrid(
  lat: number,
  lon: number,
  zoom: number,
  radius: number,
  centerLat: number,
  centerLon: number
): { left: number; top: number } {
  const n = 2 ** zoom;
  const worldX = ((lon + 180) / 360) * n * TILE_SIZE_PX;
  const latRad = (lat * Math.PI) / 180;
  const worldY =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n * TILE_SIZE_PX;
  const { x: cx, y: cy } = latLonToTileXY(centerLat, centerLon, zoom);
  const originX = (cx - radius) * TILE_SIZE_PX;
  const originY = (cy - radius) * TILE_SIZE_PX;
  return { left: worldX - originX, top: worldY - originY };
}

/** Square grid of tiles centered on lat/lon. */
export function buildTileGrid(
  lat: number,
  lon: number,
  zoom: number,
  radius = 2
): MapTileCoord[] {
  const { x: cx, y: cy } = latLonToTileXY(lat, lon, zoom);
  const tiles: MapTileCoord[] = [];
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      tiles.push({
        x: cx + dx,
        y: cy + dy,
        px: (dx + radius) * TILE_SIZE_PX,
        py: (dy + radius) * TILE_SIZE_PX,
      });
    }
  }
  return tiles;
}

export function osmTileUrl(zoom: number, x: number, y: number): string {
  return `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
}

/** Default radius view for property location. */
export function defaultRadarMapView(
  lat = WEATHER_LAT,
  lon = WEATHER_LON
): RadarMapView {
  return radarMapViewForRadiusMiles(lat, lon, DEFAULT_RADAR_RADIUS_MILES);
}
